import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Copy, Share2, ShoppingCart, Pill, Wallet, CalendarDays, Utensils, Flame } from "lucide-react";
import api from "../lib/api";
import { money } from "../lib/format";
import { CATEGORY_COLORS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";

function monthOptions() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    out.push({ val, label });
  }
  return out;
}

function Metric({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-0 bg-brand text-white" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${accent ? "text-white/80" : "text-slate-400"}`}>{label}</span>
        <Icon className={`h-3.5 w-3.5 ${accent ? "text-white/80" : "text-brand"}`} />
      </div>
      <p className="mt-1 font-mono text-lg font-extrabold">{value}</p>
    </div>
  );
}

export default function MonthlyReport({ open, onOpenChange }) {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const opts = monthOptions();
  const [month, setMonth] = useState(opts[0].val);
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/report/monthly/${m}`);
      setR(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(month); }, [open, month, load]);

  const monthLabel = opts.find((o) => o.val === month)?.label || month;

  const shareText = r ? [
    `📊 Meal Cost Report — ${monthLabel}`,
    ``,
    `🛒 Groceries bought: ${money(r.food_spent, cur)}`,
    `💊 Supplements bought: ${money(r.supplement_spent, cur)}`,
    `💰 Total spent: ${money(r.total_spent, cur)}`,
    ``,
    `🍽️ Logged ${r.days_logged} day(s) · avg ${money(r.avg_total_daily, cur)}/day`,
    `📈 At this rate: ${money(r.projected_monthly, cur)}/mo · ${money(r.projected_annual, cur)}/yr`,
    r.top_meals?.[0] ? `\n🥇 Priciest meal: ${r.top_meals[0].name} (${money(r.top_meals[0].cost, cur)})` : "",
  ].filter(Boolean).join("\n") : "";

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareText); toast.success("Report copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `Meal Cost Report — ${monthLabel}`, text: shareText }); }
      catch {}
    } else { copy(); }
  };

  const maxCat = r?.category_spend?.[0]?.amount || 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[560px]" data-testid="monthly-report-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Monthly Report</DialogTitle></DialogHeader>

        <div className="flex items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-full" data-testid="report-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{opts.map((o) => <SelectItem key={o.val} value={o.val}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {loading || !r ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <div className="space-y-4" data-testid="report-body">
            <div className="grid grid-cols-3 gap-2">
              <Metric label="Groceries" value={money(r.food_spent, cur)} icon={ShoppingCart} />
              <Metric label="Supplements" value={money(r.supplement_spent, cur)} icon={Pill} />
              <Metric label="Total Spent" value={money(r.total_spent, cur)} icon={Wallet} accent />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-500">
                <CalendarDays className="h-4 w-4 text-brand" />Logged consumption
              </div>
              <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-400">Days logged</span><p className="font-mono font-bold">{r.days_logged}</p></div>
                <div><span className="text-slate-400">Avg / day</span><p className="font-mono font-bold">{money(r.avg_total_daily, cur)}</p></div>
                <div><span className="text-slate-400">Projected / mo</span><p className="font-mono font-bold">{money(r.projected_monthly, cur)}</p></div>
                <div><span className="text-slate-400">Projected / yr</span><p className="font-mono font-bold text-brand">{money(r.projected_annual, cur)}</p></div>
              </div>
            </div>

            {r.category_spend?.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-semibold text-slate-500">Grocery spend by category</p>
                <div className="mt-3 space-y-2">
                  {r.category_spend.map((c) => (
                    <div key={c.category}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-slate-600">{c.category}</span>
                        <span className="font-mono text-slate-500">{money(c.amount, cur)}</span>
                      </div>
                      <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-brand" style={{ width: `${Math.max(6, (c.amount / maxCat) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Utensils className="h-4 w-4 text-brand" />Priciest meals</div>
                <div className="mt-2 space-y-1.5 text-sm">
                  {r.top_meals?.length ? r.top_meals.map((m, i) => (
                    <div key={i} className="flex justify-between"><span className="truncate text-slate-600">{m.name}</span><span className="font-mono font-semibold">{money(m.cost, cur)}</span></div>
                  )) : <p className="text-slate-300">—</p>}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Flame className="h-4 w-4 text-brand" />Priciest per serving</div>
                <div className="mt-2 space-y-1.5 text-sm">
                  {r.top_foods?.length ? r.top_foods.map((f, i) => (
                    <div key={i} className="flex justify-between"><span className="truncate text-slate-600">{f.name}</span><span className="font-mono font-semibold">{money(f.cost, cur)}</span></div>
                  )) : <p className="text-slate-300">—</p>}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={copy} data-testid="report-copy-button" variant="outline" className="flex-1 rounded-full border-slate-200"><Copy className="mr-1.5 h-4 w-4" />Copy</Button>
              <Button onClick={share} data-testid="report-share-button" className="flex-1 rounded-full bg-brand hover:bg-brand-hover"><Share2 className="mr-1.5 h-4 w-4" />Share</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
